import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { SignUpHeader } from '../../components/SignUpHeader';
import { PrimaryButton } from '../../components/PrimaryButton';
import { SecondaryButton } from '../../components/SecondaryButton';
import { getMe } from '../../../lib/professional-api';
import { apiErrorMessage } from '../../../lib/api';
import { toast } from '../../../lib/toast';
import { RequirePro } from '../../../lib/use-auth';

type Status = 'PENDING_DOCS' | 'DOCS_SUBMITTED' | 'VERIFIED' | 'REJECTED';

export default function VerificationCompletePage() {
  return (
    <RequirePro>
      <VerificationCompleteInner />
    </RequirePro>
  );
}

function VerificationCompleteInner() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status | null>(null);
  const [notes, setNotes] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await getMe();
        setStatus(res.info?.verification_status ?? null);
        setNotes(res.info?.verification_notes ?? null);
      } catch (err) {
        toast.error(apiErrorMessage(err, 'Could not load your status.'));
      }
    })();
  }, []);

  // Pick the headline, body and primary CTA based on where the pro is
  // in the verification pipeline.
  const view = (() => {
    if (!status) {
      return {
        title: 'Loading…',
        body: 'Fetching your verification status.',
        cta: null as { label: string; onClick: () => void } | null,
        secondary: null as { label: string; to: string } | null,
        accent: '#8e2a2f',
      };
    }
    if (status === 'VERIFIED') {
      return {
        title: 'You are verified!',
        body:
          "All your documents have been approved. You're ready to take bookings.",
        cta: {
          label: 'Go to dashboard',
          onClick: () => navigate('/professional/dashboard'),
        },
        secondary: null,
        accent: '#065f46',
      };
    }
    if (status === 'REJECTED') {
      return {
        title: 'One or more documents were rejected',
        body:
          (notes ? `Reviewer note: ${notes}. ` : '') +
          'Re-upload the documents that need attention from the identity page.',
        cta: {
          label: 'Back to documents',
          onClick: () => navigate('/professional/verify-identity'),
        },
        secondary: null,
        accent: '#991b1b',
      };
    }
    if (status === 'DOCS_SUBMITTED') {
      return {
        title: 'Documents submitted',
        body:
          "Thanks! Our team is reviewing your documents. Most reviews take 24-48 hours — we'll email you the moment you're approved.",
        cta: {
          label: 'Go to dashboard',
          onClick: () => navigate('/professional/dashboard'),
        },
        secondary: {
          label: 'Review documents',
          to: '/professional/verify-identity',
        },
        accent: '#92400e',
      };
    }
    // PENDING_DOCS — pro hasn't uploaded everything yet.
    return {
      title: "Let's finish setting up your account",
      body:
        'You still have documents to upload before customers can book you.',
      cta: {
        label: 'Continue to documents',
        onClick: () => navigate('/professional/verify-identity'),
      },
      secondary: null,
      accent: '#8e2a2f',
    };
  })();

  return (
    <div className="bg-white min-h-screen flex flex-col">
      <SignUpHeader isProfessional={true} mode="signin" />
      <main className="flex-1 flex items-center justify-center py-12 px-4">
        <div className="w-full max-w-[420px] flex flex-col items-center text-center gap-6">
          <span
            className="material-icons text-[56px]"
            style={{ color: view.accent }}
          >
            {status === 'VERIFIED'
              ? ''
              : status === 'REJECTED'
              ? ''
              : status === 'DOCS_SUBMITTED'
              ? ''
              : ''}
          </span>
          <h1
            className="font-['Playfair_Display',sans-serif] font-extrabold text-[28px] md:text-[34px] leading-[40px] md:leading-[48px]"
            style={{ color: view.accent }}
          >
            {view.title}
          </h1>
          <p className="text-[#908e8e] text-[14px] leading-[21px]">
            {view.body}
          </p>
          {view.cta ? (
            <PrimaryButton onClick={view.cta.onClick} className="w-full py-4">
              {view.cta.label}
            </PrimaryButton>
          ) : null}
          {view.secondary ? (
            <Link to={view.secondary.to} className="w-full">
              <SecondaryButton className="w-full py-4">
                {view.secondary.label}
              </SecondaryButton>
            </Link>
          ) : null}
        </div>
      </main>
    </div>
  );
}
